import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { notify } from '../../../shared/feedback/notify';
import {
  EsteiraTagDefinicao,
  getEsteiraTagDefinicoes,
  createEsteiraTagDefinicao,
  updateEsteiraTagDefinicao,
  deleteEsteiraTagDefinicao,
  addEsteiraTagOpcao,
  removeEsteiraTagOpcao,
  addEsteiraTagRole,
  removeEsteiraTagRole,
} from '../../../api/esteiraTagsApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de cabeçalho compartilhado (`recepcao-header-row`, `recepcao-title`). O import é
// explícito de propósito: contar com o CSS que a página hospedeira carrega é acoplamento
// invisível — a aba quebraria em silêncio se fosse montada em outra tela (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import { removerAcentos } from '../../../shared/texto/busca-sem-acento';

/**
 * Aba "Tags de Esteira" da Administração do Sistema.
 *
 * Extraída de `AdministracaoSistemaPage.tsx` na at-73 — a página hospedava esta aba e
 * a de Fluxos inline (1001 linhas), enquanto Checklist, Pausas Automáticas e
 * Especialidades já eram components. A extração é o que permite a página virar só o
 * seletor de abas do módulo Oficina.
 *
 * A lista de esteiras vem do hook `useEsteiras` (react-query, mesma chave de cache do
 * resto da tela) em vez de prop: evita plumbing e não gera requisição a mais.
 */

// ADMIN não aparece: o backend sempre autoriza ADMIN (esteiras e tags), marcar seria redundante.
const ALL_ROLES = [
  'RECEPCAO', 'CHEFE_DE_OFICINA', 'COLABORADOR', 'ORCAMENTO',
  'COMPRA', 'ESTOQUE',
];

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que são as tags',
    conteudo: (
      <p>
        Tags são campos extras que aparecem nos veículos <strong>enquanto estão em uma esteira específica</strong>{' '}
        — por exemplo, o motivo do aguardo na esteira de espera. Cada tag pertence a uma esteira e é preenchida
        nas telas operacionais por quem tem o perfil autorizado. O valor preenchido aparece nos cartões do Painel
        Geral.
      </p>
    ),
  },
  {
    titulo: 'Opções ou texto livre',
    conteudo: (
      <p>
        Em <strong>Opções da Tag</strong>, cadastre os valores possíveis — a tag vira uma lista de escolha. Se
        nenhuma opção for cadastrada, a tag é <strong>texto livre</strong> (campo aberto), como a própria tela
        indica.
      </p>
    ),
  },
  {
    titulo: 'Quem pode preencher',
    conteudo: (
      <p>
        <strong>Perfis autorizados a preencher</strong> define quem vê e edita a tag nas telas operacionais. O
        perfil de administrador não aparece na lista porque sempre pode preencher, sem precisar ser marcado.
      </p>
    ),
  },
  {
    titulo: 'Visível ao associado',
    conteudo: (
      <p>
        Marcando <strong>Visível ao associado</strong>, o valor preenchido nesta tag aparece ao associado na página
        de acompanhamento do veículo, como motivo da pendência da etapa atual (por exemplo, "Aguardando peça"). Só
        o valor aparece, nunca o nome da tag. A tag nasce desmarcada: marque apenas as que usam palavras que o
        associado entende.
      </p>
    ),
  },
  {
    titulo: 'Desativar em vez de excluir',
    conteudo: (
      <p>
        <strong>Desativar</strong> tira a tag de uso sem apagar os valores já preenchidos nos veículos;{' '}
        <strong>Reativar</strong> a devolve. Use o filtro <strong>Incluir tags inativas</strong> para revê-las.
        Não existe exclusão de tag — o histórico preenchido é preservado.
      </p>
    ),
  },
];

interface EsteiraTagsSectionProps {
  /** Informa o hospedeiro se esta aba ainda está carregando (usa o loader da página). */
  onLoadingChange?: (loading: boolean) => void;
}

const EsteiraTagsSection: React.FC<EsteiraTagsSectionProps> = ({ onLoadingChange }) => {
  // `true` = inclui esteiras inativas, como na listagem admin de onde esta aba saiu
  // (a mesma chamada da aba de Fluxos: mesma queryKey, sem requisição a mais).
  const { esteiras } = useEsteiras(true);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [definicoes, setDefinicoes] = useState<EsteiraTagDefinicao[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Nova tag form
  const [novoNome, setNovoNome] = useState('');
  const [novoEstTagId, setNovoEstTagId] = useState('');
  const [novosRoles, setNovosRoles] = useState<string[]>([]);
  const [criandoTag, setCriandoTag] = useState(false);
  const [novaOpcaoLabel, setNovaOpcaoLabel] = useState<Record<string, string>>({});

  const labelToValor = (label: string): string =>
    removerAcentos(label)
      .toUpperCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');

  const fetchDefinicoes = useCallback(async () => {
    setLoadingTags(true);
    try {
      const data = await getEsteiraTagDefinicoes(undefined, includeInactive);
      setDefinicoes(data);
    } catch {
      toast.error('Erro ao carregar tags de esteira.');
    } finally {
      setLoadingTags(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchDefinicoes();
  }, [fetchDefinicoes]);

  useEffect(() => {
    onLoadingChange?.(loadingTags);
  }, [loadingTags, onLoadingChange]);

  useEffect(() => {
    if (esteiras.length > 0 && !novoEstTagId) {
      const activeList = esteiras.filter((e) => e.ativo);
      if (activeList.length > 0) {
        setNovoEstTagId(activeList[0].id);
      }
    }
  }, [esteiras, novoEstTagId]);

  const handleCriarTag = async () => {
    if (!novoNome.trim()) return toast.error('Informe o nome da tag.');
    if (!novoEstTagId) return toast.error('Selecione uma esteira.');
    setCriandoTag(true);
    try {
      await createEsteiraTagDefinicao({
        esteiraId: novoEstTagId,
        nome: novoNome.trim(),
        rolesPermitidos: novosRoles,
      });
      setNovoNome('');
      setNovosRoles([]);
      toast.success('Tag criada com sucesso.');
      fetchDefinicoes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao criar tag.');
    } finally {
      setCriandoTag(false);
    }
  };

  const handleToggleAtivo = async (def: EsteiraTagDefinicao) => {
    try {
      if (def.ativo) {
        await deleteEsteiraTagDefinicao(def.id);
        toast.success(`Tag "${def.nome}" desativada.`);
      } else {
        await updateEsteiraTagDefinicao(def.id, { ativo: true });
        toast.success(`Tag "${def.nome}" reativada.`);
      }
      fetchDefinicoes();
    } catch {
      toast.error('Erro ao alterar status da tag.');
    }
  };

  /** at-126: liga/desliga a exibição do valor desta tag ao associado, como motivo da pendência. */
  const handleToggleVisivelAoAssociado = async (def: EsteiraTagDefinicao) => {
    const novo = !def.visivelAoAssociado;
    try {
      await updateEsteiraTagDefinicao(def.id, { visivelAoAssociado: novo });
      notify.success(
        novo
          ? `O valor de "${def.nome}" passa a aparecer ao associado.`
          : `O valor de "${def.nome}" deixa de aparecer ao associado.`,
      );
      fetchDefinicoes();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível alterar a exibição ao associado. Tente de novo.');
    }
  };

  const handleAdicionarOpcao = async (defId: string) => {
    const label = novaOpcaoLabel[defId]?.trim();
    if (!label) return toast.error('Informe o nome da opção.');
    const valor = labelToValor(label);
    if (!valor) return toast.error('Não foi possível gerar um valor interno a partir desse nome.');
    try {
      await addEsteiraTagOpcao(defId, { label, valor });
      setNovaOpcaoLabel((p) => ({ ...p, [defId]: '' }));
      toast.success('Opção adicionada.');
      fetchDefinicoes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao adicionar opção.');
    }
  };

  const handleRemoverOpcao = async (defId: string, opcaoId: string) => {
    try {
      await removeEsteiraTagOpcao(defId, opcaoId);
      toast.success('Opção removida.');
      fetchDefinicoes();
    } catch {
      toast.error('Erro ao remover opção.');
    }
  };

  const handleAddRole = async (defId: string, role: string) => {
    try {
      await addEsteiraTagRole(defId, role);
      fetchDefinicoes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao adicionar role.');
    }
  };

  const handleRemoveRole = async (defId: string, role: string) => {
    try {
      await removeEsteiraTagRole(defId, role);
      fetchDefinicoes();
    } catch {
      toast.error('Erro ao remover role.');
    }
  };

  const grouped = esteiras.reduce<Record<string, EsteiraTagDefinicao[]>>((acc, est) => {
    acc[est.id] = definicoes.filter((d) => d.esteiraId === est.id);
    return acc;
  }, {});

  return (
    <>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Tags de Esteira
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/* Form Criar Tag */}
      <div className="admin-card">
        <div className="admin-card-title">Criar Nova Tag de Esteira</div>
        <div className="admin-form-grid">
          <div className="admin-input-group fixed-width">
            <label className="admin-label">Esteira</label>
            <select
              value={novoEstTagId}
              onChange={(e) => setNovoEstTagId(e.target.value)}
              className="admin-select"
            >
              {esteiras.filter((e) => e.ativo).map((est) => (
                <option key={est.id} value={est.id}>
                  {est.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-input-group">
            <label className="admin-label">Nome da Tag</label>
            <input
              type="text"
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              placeholder="Ex: Motivo do Aguardo"
              className="admin-input"
            />
          </div>

          <button
            onClick={handleCriarTag}
            disabled={criandoTag}
            className="admin-btn admin-btn-primary"
            style={{ height: '40px' }}
          >
            {criandoTag ? 'Criando...' : 'Criar Tag'}
          </button>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="admin-label" style={{ marginBottom: '8px', display: 'block' }}>
            Perfis autorizados a preencher
          </label>
          <div className="admin-checkbox-group">
            {ALL_ROLES.map((role) => (
              <label key={role} className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={novosRoles.includes(role)}
                  onChange={(e) =>
                    setNovosRoles((prev) =>
                      e.target.checked ? [...prev, role] : prev.filter((r) => r !== role)
                    )
                  }
                />
                {role}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Filtro incluir inativas */}
      <div style={{ marginBottom: '20px' }}>
        <label className="admin-checkbox-label">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Incluir tags inativas
        </label>
      </div>

      {/* Listagem de Tags */}
      {esteiras.map((est) => {
        const tags = grouped[est.id] || [];
        if (tags.length === 0) return null;
        return (
          <div key={est.id} className="admin-group-section">
            <div className="admin-group-title">
              {est.nome}
            </div>

            {tags.map((def) => (
              <div
                key={def.id}
                className="admin-card"
                style={{ opacity: def.ativo ? 1 : 0.6, marginBottom: '14px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '16px', color: 'var(--tema-texto, #1A1C1B)' }}>
                      {def.nome}
                    </span>
                    {!def.ativo && <span className="admin-badge-inactive">INATIVA</span>}
                  </div>
                  <button
                    onClick={() => handleToggleAtivo(def)}
                    className={`admin-btn ${def.ativo ? 'admin-btn-danger-outline' : 'admin-btn-success-outline'}`}
                    style={{ padding: '6px 16px', fontSize: '12px' }}
                  >
                    {def.ativo ? 'Desativar' : 'Reativar'}
                  </button>
                </div>

                {/* Perfis */}
                <div style={{ marginTop: '16px' }}>
                  <div className="admin-label" style={{ color: 'var(--tema-texto-2, #5E5E5E)', marginBottom: '8px' }}>
                    Perfis autorizados a preencher:
                  </div>
                  <div className="admin-role-pills">
                    {ALL_ROLES.map((role) => {
                      const isPermitted = def.rolesPermitidos.some((r) => r.role === role);
                      return (
                        <button
                          key={role}
                          onClick={() =>
                            isPermitted ? handleRemoveRole(def.id, role) : handleAddRole(def.id, role)
                          }
                          className={`admin-role-pill ${isPermitted ? 'active' : ''}`}
                        >
                          {isPermitted ? '✓ ' : ''}
                          {role}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* at-126: exibição ao associado no acompanhamento */}
                <div style={{ marginTop: '16px' }}>
                  <label className="admin-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={!!def.visivelAoAssociado}
                      onChange={() => handleToggleVisivelAoAssociado(def)}
                    />
                    Visível ao associado
                  </label>
                  <p className="admin-hint">
                    Marcada, o valor desta tag aparece ao associado na página de acompanhamento, como motivo da
                    pendência da etapa. Nasce desmarcada.
                  </p>
                </div>

                {/* Opções */}
                <div style={{ marginTop: '20px', borderTop: '1px solid var(--tema-linha, #f1f3f4)', paddingTop: '16px' }}>
                  <div className="admin-label" style={{ color: 'var(--tema-texto-2, #5E5E5E)', marginBottom: '8px' }}>
                    Opções da Tag (se vazio = texto livre):
                  </div>
                  <div className="admin-tags-row">
                    {def.opcoes.length === 0 && (
                      <span style={{ fontSize: '13px', color: 'var(--tema-texto-4, #939393)', fontStyle: 'italic' }}>
                        Texto livre (campo aberto)
                      </span>
                    )}
                    {def.opcoes.map((op: any) => (
                      <span key={op.id || op.valor} className="admin-tag-pill">
                        {op.label}
                        <button
                          onClick={() => handleRemoverOpcao(def.id, op.id)}
                          className="admin-tag-remove-btn"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
                    <input
                      type="text"
                      placeholder="Nova opção"
                      value={novaOpcaoLabel[def.id] ?? ''}
                      onChange={(e) =>
                        setNovaOpcaoLabel((p) => ({ ...p, [def.id]: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === 'Enter' && handleAdicionarOpcao(def.id)}
                      className="admin-input"
                      style={{ width: '220px', height: '36px', padding: '6px 12px', fontSize: '13px' }}
                    />
                    <button
                      onClick={() => handleAdicionarOpcao(def.id)}
                      className="admin-btn admin-btn-primary"
                      style={{ height: '36px', padding: '0 16px', fontSize: '13px' }}
                    >
                      + Opção
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Tags de Esteira"
        secoes={helpSections}
      />
    </>
  );
};

export default EsteiraTagsSection;
